import * as React from "react";
import { toast } from "@grandvisionhq/common";
import { useMutation } from "@grandvisionhq/graphql";
import { useCartStorage } from "@grandvisionhq/lib-utils-cart";
import { useDispatchTrackErrorEvent } from "@grandvisionhq/svc-tealium/events";
import { errorsMapping } from "../../../../mappers/mappers/errors-mapping.js";
import { useMutationConfig } from "./use-mutation-config.js";
import { useUpdateCartCache } from "./use-update-cart-cache.js";
import { getFlattenedCartFailureErrorCodes } from "../../../../utils/utils/get-flattened-cart-error-codes.js";
import { SET_SHIPPING_METHOD } from "../../../../operations/mutations/operations/mutations/set-shipping-method.js";
const useSetShippingMethod = ({ onError, onCompleted } = {}) => {
  const [{ cartId, cartHash }] = useCartStorage();
  const { updateCacheWithCart } = useUpdateCartCache();
  const [selectedShippingMethod, setSelectedShippingMethod] = React.useState(void 0);
  const dispatchTrackErrorEvent = useDispatchTrackErrorEvent();
  const handleOnCompleted = React.useCallback(
    (responseData) => {
      let result;
      if ("setShippingMethod" in responseData) {
        result = responseData.setShippingMethod;
        if (result.failures?.length) {
          const errorCodes = getFlattenedCartFailureErrorCodes(result.failures);
          onError?.({ reason: "MUTATION" });
          errorCodes.forEach((error) => toast.failed(error));
        }
        if (result.success) {
          selectedShippingMethod && onCompleted?.("MUTATION", selectedShippingMethod);
        }
      }
    },
    [onCompleted, onError, selectedShippingMethod]
  );
  const handleOnError = React.useCallback(() => {
    onError?.({ reason: "MUTATION" });
    dispatchTrackErrorEvent(
      () => Promise.resolve({
        Error_Source: "Server",
        Error_Code: "Set shipping method error",
        Error_Details: "Mutation failed",
        Error_Message: errorsMapping.SOMETHING_WENT_WRONG
      })
    );
    toast.failed(errorsMapping.SOMETHING_WENT_WRONG);
  }, [dispatchTrackErrorEvent, onError]);
  const updateCache = React.useCallback(
    (cache, { data }) => {
      if (data && data.setShippingMethod.success) {
        const updatedCart = data.setShippingMethod.cart;
        updatedCart && updateCacheWithCart({ cart: updatedCart, cache });
      }
    },
    [updateCacheWithCart]
  );
  const { mutationOptions } = useMutationConfig({
    onError: handleOnError,
    onCompleted: handleOnCompleted
  });
  const [mutationCallback, mutation] = useMutation(SET_SHIPPING_METHOD, {
    ...mutationOptions,
    // reason: typescript complains about a typing mismatch for the data prop
    update: updateCache,
    context: { forwardHeaders: { "cart-hash": cartHash } }
  });
  const setShippingMethod = React.useCallback(
    async (shippingMethodId, globalStoreId) => {
      await mutationCallback({
        variables: {
          shippingMethodInput: {
            shippingMethodId,
            checkoutId: cartId,
            globalStoreId
          }
        }
      });
    },
    [mutationCallback, cartId]
  );
  return {
    setShippingMethod,
    isUpdating: mutation.loading,
    updateError: mutation.error,
    setSelectedShippingMethod
  };
};
export {
  useSetShippingMethod
};
//# sourceMappingURL=use-set-shipping-method.js.map
